import React, { useCallback, useRef, useState } from 'react'
import { MoreFilterTopBar } from '../components/MoreFilterTopBar'
import { map } from 'lodash'
import { CNFAttributeFilterValueRow } from '../../components/CNFAttributeFilterValueRow'
import { CentricTypography as Typography } from '@core/component/CentricTypography'
import { makeStyles } from '@material-ui/core'
import { useObserver } from 'mobx-react-lite'
import { NoRecordFound } from '@components/UI-Components/NoRecordFound'
import { MoreFilterLetterJump } from '../components/MoreFilterLetterJump'
import { isEmptyValue } from '@utils/object-utils'

const ROW_HEIGHT = 30
const ROW_HEIGHT_FOR_TABLET_VIEW = 44

const useStyles = makeStyles(theme => ({
  moreFilterContainer: {
    width: '100%',
    height: '100%'
  },
  moreFilterBody: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    height: 'calc(100% - 48px)',
    width: '100%'
  },
  tabletBody: {
    width: 'calc(100% - 41px)',
    display: 'flex',
    flexWrap: 'wrap',
    overflowY: 'auto',
    padding: '6px 0px 0px',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  normalBody: {
    height: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    overflowX: 'auto',
    padding: '6px 0px 0px'
  },
  row: {
    width: 300,
    padding: '0px 15px'
  },
  tabletRow: {
    width: '100%',
    padding: '0px 15px'
  },
  letterHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 24
  },
  tabletLetterWrapper: {
    backgroundColor: '#EEEEEE',
    width: '41px',
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 0px'
  }
}))

export const MoreFilterContainer = ({
  filterValues,
  filterId,
  filterLabel,
  filterStore,
  defaultEmptyText,
  handleFilterSelection,
  handleFilterExclusion,
  setAnchorEl,
  isTablet
}) => {
  return useObserver(function useHook() {
    const ref = useRef(null)

    const {
      selected,
      excludedFilters,
      getData,
      onMoreFilterSearchKeyChange,
      moreFilterSearchKey,
      getSearchedData,
      resetFilterSearchKey
    } = filterStore

    const [letterToHighlight, setLetterToHighlight] = useState('')
    const classes = useStyles()

    let searchedMoreFilterValues = filterValues
    if (!!moreFilterSearchKey) {
      //  setLetterToHighlight('')
      searchedMoreFilterValues = getSearchedData(filterValues, moreFilterSearchKey)
    }

    const sequentialFilterValues = getData(
      searchedMoreFilterValues || [],
      defaultEmptyText
    )
    const handleLetterSelect = letter => {
      letterToHighlight !== letter && !isTablet
        ? setLetterToHighlight(letter)
        : setLetterToHighlight('')
      const elem = document.getElementById(`moreScroll_${letter}`)
      ref.current = elem
      if (ref.current)
        ref.current.scrollIntoView({
          behaviour: 'smooth',
          block: isTablet ? 'start' : 'end'
        })
    }
    const handleDocumentClick = useCallback(
      e => {
        const selectedNodes = document.querySelectorAll(
          `#moreScroll_${letterToHighlight}`
        )
        let isHighlightedSection = false
        selectedNodes.forEach(node => {
          if (node && node.contains(e.target)) {
            isHighlightedSection = true
          }
        })
        if (!isHighlightedSection) setLetterToHighlight('')
      },
      [letterToHighlight]
    )
    return (
      <div className={classes.moreFilterContainer}>
        <MoreFilterTopBar
          data={sequentialFilterValues}
          filterLabel={filterLabel}
          handleMoreFilterPopup={open => {
            setAnchorEl(open)
            resetFilterSearchKey()
          }}
          onHandleLetter={handleLetterSelect}
          onMoreFilterSearchKeyChange={onMoreFilterSearchKeyChange}
          letterToHighlight={letterToHighlight}
          handleDocumentClick={handleDocumentClick}
          moreFilterSearchKey={moreFilterSearchKey}
          setLetterToHighlight={setLetterToHighlight}
          isTablet={isTablet}
        />
        <div
          className={classes.moreFilterBody}
          ref={ref}
          onClick={e => handleDocumentClick(e)}
        >
          <div className={isTablet ? classes.tabletBody : classes.normalBody}>
            {sequentialFilterValues?.length ? (
              map(sequentialFilterValues, item => (
                <div
                  key={item?.value}
                  className={
                    isTablet && item?.type === 'header' ? classes.tabletRow : classes.row
                  }
                  id={`moreScroll_${item?.id || item?.initial}`}
                >
                  {item?.type === 'header' ? (
                    <div
                      className={classes.letterHeader}
                      style={{
                        height: isTablet ? ROW_HEIGHT_FOR_TABLET_VIEW : ROW_HEIGHT
                      }}
                    >
                      <Typography
                        variant={
                          letterToHighlight && letterToHighlight !== item?.id
                            ? 'h6LightGrey'
                            : 'h6'
                        }
                      >
                        {item?.name}
                      </Typography>
                    </div>
                  ) : (
                    <CNFAttributeFilterValueRow
                      id={item?.value}
                      name={isEmptyValue(item?.value) ? defaultEmptyText : item?.value}
                      attributeId={filterId}
                      handleCheckboxSelection={(e, checked) => {
                        handleFilterSelection(checked, item, filterId)
                      }}
                      isSelected={
                        !!selected?.[filterId]?.some(ele => ele.value === item?.value)
                      }
                      canShowCheckbox={true}
                      handleFilterExclusion={() => {
                        handleFilterExclusion(item, filterId)
                      }}
                      isExcluded={
                        !!excludedFilters?.[filterId]?.some(
                          ele => ele.value === item.value
                        )
                      }
                      isDisabled={
                        letterToHighlight && letterToHighlight !== item?.initial
                      }
                      filterCount={item?.count}
                      rowHeight={isTablet ? 44 : 30}
                    />
                  )}
                </div>
              ))
            ) : (
              <div style={{ margin: 20 }}>
                <NoRecordFound />
              </div>
            )}
          </div>
          {isTablet && (
            <div className={classes.tabletLetterWrapper}>
              <MoreFilterLetterJump
                data={sequentialFilterValues}
                onHandleLetter={handleLetterSelect}
                letterToHighlight={letterToHighlight}
                setLetterToHighlight={setLetterToHighlight}
                isTablet={isTablet}
              />
            </div>
          )}
        </div>
      </div>
    )
  })
}
